import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import type { Json } from '@/integrations/supabase/types';

// ─── Frameworks (change_management_progress) ───

interface Task {
  id: string;
  text: string;
  checked: boolean;
  aiGenerated?: boolean;
  recommendation?: string;
}

interface Phase {
  id: number;
  name: string;
  description: string;
  kotterStep: string;
  adkarLetter: string;
  adkarName: string;
  color: string;
  tasks: Task[];
}

const defaultPhases: Phase[] = [
  {
    id: 1, name: 'Preparación', description: 'Crear urgencia y formar coalición de cambio',
    kotterStep: 'Pasos 1-2: Urgencia + Coalición', adkarLetter: 'A', adkarName: 'Awareness (Conciencia)',
    color: 'from-amber-500 to-orange-500',
    tasks: [
      { id: '1-1', text: 'Presentar resultados del diagnóstico de madurez IA al comité directivo', checked: false },
      { id: '1-2', text: 'Identificar y reunir a los líderes de cambio (coalición)', checked: false },
      { id: '1-3', text: 'Comunicar los riesgos de no adoptar IA (basado en análisis de riesgos)', checked: false },
      { id: '1-4', text: 'Mapear stakeholders clave y su nivel de influencia', checked: false },
      { id: '1-5', text: 'Realizar kickoff de transformación con la coalición', checked: false },
    ]
  },
  {
    id: 2, name: 'Visión y Estrategia', description: 'Definir visión IA y comunicarla efectivamente',
    kotterStep: 'Pasos 3-4: Visión + Comunicación', adkarLetter: 'D', adkarName: 'Desire (Deseo)',
    color: 'from-blue-500 to-cyan-500',
    tasks: [
      { id: '2-1', text: 'Definir la visión IA de la organización (alineada con la configuración del CoE)', checked: false },
      { id: '2-2', text: 'Crear plan de comunicación con mensajes clave por audiencia', checked: false },
      { id: '2-3', text: 'Mostrar beneficios concretos: proyectos piloto del portafolio como ejemplo', checked: false },
      { id: '2-4', text: 'Realizar sesión de Q&A abierta para resolver dudas y resistencias', checked: false },
      { id: '2-5', text: 'Involucrar voluntarios para los primeros pilotos de IA', checked: false },
    ]
  },
  {
    id: 3, name: 'Ejecución', description: 'Eliminar obstáculos y generar victorias rápidas',
    kotterStep: 'Pasos 5-6: Obstáculos + Quick Wins', adkarLetter: 'K', adkarName: 'Knowledge (Conocimiento)',
    color: 'from-emerald-500 to-teal-500',
    tasks: [
      { id: '3-1', text: 'Ejecutar ruta de capacitación en herramientas de IA priorizadas', checked: false },
      { id: '3-2', text: 'Remover barreras identificadas en el diagnóstico (accesos, permisos, herramientas)', checked: false },
      { id: '3-3', text: 'Lanzar al menos 2 proyectos Quick Win del portafolio', checked: false },
      { id: '3-4', text: 'Documentar y comunicar las primeras victorias y resultados medibles', checked: false },
      { id: '3-5', text: 'Reconocer públicamente a los agentes de cambio', checked: false },
    ]
  },
  {
    id: 4, name: 'Sostenibilidad', description: 'Consolidar ganancias y anclar en la cultura',
    kotterStep: 'Pasos 7-8: Consolidar + Anclar', adkarLetter: 'A+R', adkarName: 'Ability + Reinforcement',
    color: 'from-violet-500 to-purple-500',
    tasks: [
      { id: '4-1', text: 'Establecer métricas de adopción sostenida y revisarlas mensualmente', checked: false },
      { id: '4-2', text: 'Incorporar uso de IA en evaluaciones de desempeño', checked: false },
      { id: '4-3', text: 'Crear sistema de reconocimiento para adopción de IA', checked: false },
      { id: '4-4', text: 'Escalar proyectos exitosos a nuevas áreas del portafolio', checked: false },
      { id: '4-5', text: 'Realizar retrospectiva y actualizar plan de cambio', checked: false },
    ]
  },
];

export function useFrameworksProgress(organizationId: string | null) {
  const [phases, setPhases] = useState<Phase[]>(defaultPhases);
  const [loading, setLoading] = useState(true);
  const [recordId, setRecordId] = useState<string | null>(null);

  // Load from DB
  useEffect(() => {
    if (!organizationId || organizationId === 'all') {
      setPhases(defaultPhases);
      setRecordId(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from('change_management_progress')
        .select('*')
        .eq('organization_id', organizationId)
        .maybeSingle();

      if (cancelled) return;

      if (error) {
        console.error('[frameworks] load error:', error);
        setLoading(false);
        return;
      }

      if (data) {
        setRecordId(data.id);
        const stored = data.phases_data as unknown as Phase[];
        if (Array.isArray(stored) && stored.length > 0) {
          setPhases(stored);
        } else {
          setPhases(defaultPhases);
        }
      } else {
        // No record yet — use defaults, will create on first save
        setRecordId(null);
        setPhases(defaultPhases);
      }
      setLoading(false);
    };

    load();
    return () => { cancelled = true; };
  }, [organizationId]);

  const overallProgress = useMemo(() => {
    const total = phases.reduce((s, p) => s + p.tasks.length, 0);
    const checked = phases.reduce((s, p) => s + p.tasks.filter(t => t.checked).length, 0);
    return total === 0 ? 0 : Math.round((checked / total) * 100);
  }, [phases]);

  // Persist to DB
  const savePhases = useCallback(async (newPhases: Phase[]) => {
    if (!organizationId || organizationId === 'all') return;

    const total = newPhases.reduce((s, p) => s + p.tasks.length, 0);
    const checked = newPhases.reduce((s, p) => s + p.tasks.filter(t => t.checked).length, 0);
    const progress = total === 0 ? 0 : Math.round((checked / total) * 100);

    if (recordId) {
      await supabase
        .from('change_management_progress')
        .update({
          phases_data: newPhases as unknown as Json,
          overall_progress: progress,
        })
        .eq('id', recordId);
    } else {
      const { data } = await supabase
        .from('change_management_progress')
        .insert({
          organization_id: organizationId,
          phases_data: newPhases as unknown as Json,
          overall_progress: progress,
        })
        .select('id')
        .single();
      if (data) setRecordId(data.id);
    }
  }, [organizationId, recordId]);

  const toggleTask = useCallback((phaseId: number, taskId: string) => {
    setPhases(prev => {
      const next = prev.map(phase => {
        if (phase.id !== phaseId) return phase;
        return { ...phase, tasks: phase.tasks.map(t => t.id === taskId ? { ...t, checked: !t.checked } : t) };
      });
      savePhases(next);
      return next;
    });
  }, [savePhases]);

  const addTasks = useCallback((phaseId: number, newTasks: Task[]) => {
    setPhases(prev => {
      const next = prev.map(phase => {
        if (phase.id !== phaseId) return phase;
        return { ...phase, tasks: [...phase.tasks, ...newTasks] };
      });
      savePhases(next);
      return next;
    });
  }, [savePhases]);

  return { phases, loading, overallProgress, toggleTask, addTasks };
}

// ─── Role Redesign (coe_roles) ───

export interface RoleTask {
  name: string;
  description: string;
  category: 'Automatizable' | 'Aumentable' | 'Exclusivamente Humana';
  timePercent: number;
}

export interface CoeRole {
  id: string;
  current_role: string;
  new_role: string | null;
  new_focus: string | null;
  department: string;
  description: string;
  tasks: RoleTask[];
  new_tasks: RoleTask[];
  recommended_skills: { name: string; description: string; priority: string }[];
  source: string;
}


export function useCoeRoles(organizationId: string | null) {
  const [roles, setRoles] = useState<CoeRole[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!organizationId || organizationId === 'all') {
      setRoles([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from('coe_roles')
        .select('*')
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: true });

      if (cancelled) return;
      if (error) {
        console.error('[coe_roles] load error:', error);
        setLoading(false);
        return;
      }

      const mapped: CoeRole[] = (data || []).map(r => ({
        id: r.id,
        current_role: r.current_role || '',
        new_role: r.new_role,
        new_focus: r.new_focus,
        department: r.department || '',
        description: (r as any).description || '',
        tasks: (r.tasks as unknown as RoleTask[]) || [],
        new_tasks: ((r as any).new_tasks as unknown as RoleTask[]) || [],
        recommended_skills: (r.recommended_skills as unknown as { name: string; description: string; priority: string }[]) || [],
        source: r.source || 'manual',
      }));

      setRoles(mapped);
      setLoading(false);
    };

    load();
    return () => { cancelled = true; };
  }, [organizationId]);

  const addRole = useCallback(async (name: string, department: string, description: string = '') => {
    if (!organizationId || organizationId === 'all') return;
    const { data, error } = await supabase
      .from('coe_roles')
      .insert({
        organization_id: organizationId,
        current_role: name,
        department,
        description,
        tasks: [] as unknown as Json,
        recommended_skills: [] as unknown as Json,
        source: 'manual' as const,
      } as any)
      .select()
      .single();

    if (error) { toast.error('Error al crear rol'); return; }
    if (data) {
      const mapped: CoeRole = {
        id: data.id,
        current_role: data.current_role || '',
        new_role: data.new_role,
        new_focus: data.new_focus,
        department: data.department || '',
        description: (data as any).description || '',
        tasks: (data.tasks as unknown as RoleTask[]) || [],
        new_tasks: ((data as any).new_tasks as unknown as RoleTask[]) || [],
        recommended_skills: (data.recommended_skills as unknown as { name: string; description: string; priority: string }[]) || [],
        source: data.source || 'manual',
      };

      setRoles(prev => [...prev, mapped]);
    }
    return data?.id;
  }, [organizationId]);

  const updateRoleDetails = useCallback(async (roleId: string, name: string, department: string, description: string) => {
    const { error } = await supabase
      .from('coe_roles')
      .update({ current_role: name, department, description } as any)
      .eq('id', roleId);
    if (error) { toast.error('Error al actualizar rol'); return; }
    setRoles(prev => prev.map(r => r.id === roleId ? { ...r, current_role: name, department, description } : r));
  }, []);

  const deleteRole = useCallback(async (roleId: string) => {
    const { error } = await supabase.from('coe_roles').delete().eq('id', roleId);
    if (error) { toast.error('Error al eliminar rol'); return; }
    setRoles(prev => prev.filter(r => r.id !== roleId));
  }, []);

  const updateRoleTasks = useCallback(async (roleId: string, tasks: RoleTask[]) => {
    const { error } = await supabase
      .from('coe_roles')
      .update({ tasks: tasks as unknown as Json })
      .eq('id', roleId);
    if (error) { toast.error('Error al actualizar tareas'); return; }
    setRoles(prev => prev.map(r => r.id === roleId ? { ...r, tasks } : r));
  }, []);

  const updateRoleRedesign = useCallback(async (
    roleId: string,
    newRole: string,
    newFocus: string,
    recommendedSkills: { name: string; description: string; priority: string }[],
    source: string = 'ai',
    newTasks: RoleTask[] = []
  ) => {
    const { error } = await supabase
      .from('coe_roles')
      .update({
        new_role: newRole,
        new_focus: newFocus,
        recommended_skills: recommendedSkills as unknown as Json,
        new_tasks: newTasks as unknown as Json,
        source: source as any,
      } as any)
      .eq('id', roleId);
    if (error) { toast.error('Error al guardar rediseño'); return; }
    setRoles(prev => prev.map(r => r.id === roleId ? { ...r, new_role: newRole, new_focus: newFocus, recommended_skills: recommendedSkills, new_tasks: newTasks, source } : r));
  }, []);


  return { roles, loading, addRole, deleteRole, updateRoleTasks, updateRoleRedesign, updateRoleDetails };
}

// ─── Summary progress for cards ───
export function useChangeManagementSummary(organizationId: string | null, refreshKey: number = 0) {
  const [frameworkProgress, setFrameworkProgress] = useState(0);
  const [rolesCount, setRolesCount] = useState(0);
  const [rolesRedesigned, setRolesRedesigned] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);

      if (!organizationId) {
        // Super Admin "all" view — aggregate across orgs
        const [progressRes, rolesRes, redesignedRes] = await Promise.all([
          supabase.from('change_management_progress').select('overall_progress'),
          supabase.from('coe_roles').select('id', { count: 'exact', head: true }),
          supabase.from('coe_roles').select('id', { count: 'exact', head: true }).not('new_role', 'is', null),
        ]);

        const progresses = progressRes.data || [];
        const avg = progresses.length > 0
          ? Math.round(progresses.reduce((s, r) => s + (r.overall_progress ?? 0), 0) / progresses.length)
          : 0;
        setFrameworkProgress(avg);
        setRolesCount(rolesRes.count ?? 0);
        setRolesRedesigned(redesignedRes.count ?? 0);
      } else {
        const [progressRes, rolesRes, redesignedRes] = await Promise.all([
          supabase
            .from('change_management_progress')
            .select('overall_progress')
            .eq('organization_id', organizationId)
            .maybeSingle(),
          supabase
            .from('coe_roles')
            .select('id', { count: 'exact', head: true })
            .eq('organization_id', organizationId),
          supabase
            .from('coe_roles')
            .select('id', { count: 'exact', head: true })
            .eq('organization_id', organizationId)
            .not('new_role', 'is', null),
        ]);

        setFrameworkProgress(progressRes.data?.overall_progress ?? 0);
        setRolesCount(rolesRes.count ?? 0);
        setRolesRedesigned(redesignedRes.count ?? 0);
      }

      setLoading(false);
    };

    load();
  }, [organizationId, refreshKey]);

  return { frameworkProgress, rolesCount, rolesRedesigned, loading };
}
